<template>
  <div class="stock-thread-header">
    <!-- Iframe para el gráfico del stock -->
    <div class="stock-chart-container">
      <!-- Indicador de carga del iframe -->
      <div v-if="isIframeLoading" class="iframe-loading-indicator">
        <div class="wave"></div>
        <div class="wave"></div>
        <div class="wave"></div>
      </div>
      
      <iframe 
        :src="chartUrl" 
        frameborder="0" 
        class="stock-chart-iframe"
        :class="{'iframe-loading': isIframeLoading, 'iframe-loaded': !isIframeLoading}"
        @load="handleIframeLoad"
      ></iframe>
    </div>

    <!-- Información general del stock -->
    <div v-if="stockData" class="stock-info-container">
      <div class="stock-info-section general-info">
        <h3 class="section-title typo-headline-6">Información General</h3>
        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">Ticker:</span>
            <span class="info-value">{{ stockData.ticker }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Nombre:</span>
            <span class="info-value">{{ stockData.nom }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">País:</span>
            <span class="info-value">{{ stockData.country }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Índice:</span>
            <span class="info-value">{{ stockData.index_valor }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Sector:</span>
            <span class="info-value">{{ stockData.sector }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Industria:</span>
            <span class="info-value">{{ stockData.industria }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Tipo:</span>
            <span class="info-value">{{ stockData.tipus }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Divisa:</span>
            <span class="info-value">{{ stockData.divisa }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Valor actual:</span>
            <span class="info-value">{{ formatNumericValue(stockData.valor_actual) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">TAE:</span>
            <span class="info-value" :class="parseFloat(stockData.tae) > 0 ? 'positive-variation' : 'negative-variation'">{{ formatPercentage(stockData.tae) }}%</span>
          </div>
        </div>
      </div>

      <!-- Información operacional del stock -->
      <div class="stock-info-section operational-info">
        <h3 class="section-title typo-headline-6">Información Operacional</h3>
        <div class="info-grid">
          <!-- <div class="info-item">
            <span class="info-label">Dirección:</span>
            <span class="info-value">{{ getDirectionText(stockData.direccio) }}</span>
          </div> -->
          <div class="info-item">
            <span class="info-label">Tipo de entrada:</span>
            <span class="info-value">{{ stockData.entry_type_name }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Fecha análisis:</span>
            <span class="info-value">{{ formatDate(stockData.data_analisi) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Precio entrada:</span>
            <span class="info-value">{{ formatNumericValue(stockData.entrada) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Soporte:</span>
            <span class="info-value">{{ formatNumericValue(stockData.soport) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Fecha soporte:</span>
            <span class="info-value">{{ formatDateDashes(stockData.data_soport) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">LT Inicial:</span>
            <span class="info-value">{{ formatNumericValue(stockData.lt_ini) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">LT Final:</span>
            <span class="info-value">{{ formatNumericValue(stockData.lt_fi) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Barra inicial LT:</span>
            <span class="info-value">{{ stockData.lt_pos_chart_ini }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Barra final LT:</span>
            <span class="info-value">{{ stockData.lt_pos_chart_fi }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Fecha inicial LT:</span>
            <span class="info-value">{{ formatDateDashes(stockData.lt_pos_ini) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">Fecha final LT:</span>
            <span class="info-value">{{ formatDateDashes(stockData.lt_pos_fi) }}</span>
          </div>
        </div>
      </div>

      <!-- Acciones para el stock -->
      <div class="stock-actions">
        <button class="action-btn view-chart-btn" @click="viewChart">
          <i class="material-icons">show_chart</i>
          Ver gráfico
        </button>
        <button class="action-btn open-dynamic-btn" @click="openDynamicChart">
          <i class="material-icons">timeline</i>
          Abrir en graficador dinámico
        </button>
        <button class="action-btn add-favorite-btn" @click="addToFavorites">
          <i class="material-icons">star</i>
          Añadir a favoritos
        </button>
        <button class="action-btn add-watchlist-btn" @click="addToWatchlist">
          <i class="material-icons">visibility</i>
          Añadir a valores en espera
        </button>
      </div>
    </div>

    <!-- Estado de carga -->
    <div v-else-if="loading" class="loading-state">
      <p class="typo-body-2">Cargando información del valor...</p>
    </div>

    <!-- Estado de error -->
    <div v-else-if="error" class="error-state">
      <p class="typo-body-2">Error al cargar la información del valor.</p>
    </div>
    
    <!-- Componentes para favoritos y valores en espera -->
    <Workspaces
      ref="workspacesComponent"
      :show_workspace="showWorkspaces"
      @_closeFavoriteWorkspacesWindow="_closeFavoriteWorkspacesWindow"
    />
    
    <WaitingValuesAdd
      ref="waitingValuesComponent"
      :stock="stockData"
      :show_waiting="showWaiting"
      @_closeWaitingValueWindow="_closeWaitingValueWindow"
      @waiting-added="_onWaitingAdded"
    />
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
const axios = forumApi;
import { mapGetters, mapMutations } from 'vuex';
import Workspaces from '@/components/stockchart/components/Workspaces.vue';
import WaitingValuesAdd from '@/components/stockchart/components/WaitingValuesAdd.vue';

export default {
  name: 'StockThreadHeader',
  components: {
    Workspaces,
    WaitingValuesAdd
  },
  props: {
    threadId: {
      type: [String, Number],
      required: true
    },
    threadTitle: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null,
      chartUrl: '',
      isIframeLoading: true,
      lastUrlStock: '',
      showWorkspaces: false,
      showWaiting: false
    };
  },
  computed: {
    ...mapGetters(['_g_SelectedTicker']),
    
    // Local computed property derived from the store's selected ticker
    stockData() {
      return this._g_SelectedTicker && this._g_SelectedTicker.ticker === this.ticker 
             ? this._g_SelectedTicker 
             : null;
    },
    
    ticker() {
      if (!this.threadTitle) return '';
      
      // Extraer todo hasta el primer espacio en blanco o paréntesis
      const match = this.threadTitle.match(/^(\S+)(?:\s|\(|$)/);
      return match ? match[1] : '';
    }
  },
  watch: {
    chartUrl: {
      handler: function(newVal, oldVal) {
        if (newVal !== oldVal && newVal !== this.lastUrlStock) {
          this.isIframeLoading = true;
          this.lastUrlStock = newVal;
        }
      }
    },
    ticker: {
      immediate: true, // Run on component mount as well
      handler: function(newVal) {
        if (newVal) {
          // Update chart URL whenever ticker changes
          this.updateChartUrl();
          // Fetch data and update store if the ticker derived from props
          // is different from the one currently in the store
          if (!this._g_SelectedTicker || this._g_SelectedTicker.ticker !== newVal) {
            this.fetchAndSetStockData();
          }
        }
      }
    },
    // Watch for when _g_SelectedTicker changes *from the store*
    // This handles cases where another component updates the ticker
    '_g_SelectedTicker': {
      handler: function(newVal) {
        // If the store ticker changes and matches this component's ticker,
        // ensure loading/error states are cleared.
        if (newVal && newVal.ticker === this.ticker) {
           this.loading = false;
           this.error = null;
           // Update chart URL if necessary (though 'ticker' watcher should handle this)
           this.updateChartUrl(); 
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapMutations(['setSelectedTicker']),
    
    // Format functions to display data correctly
    formatPercentage(value) {
      return value ? parseFloat(value).toFixed(2) : '0.00';
    },
    
    formatNumericValue(value) {
      if (!value) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '';
      
      // Convert YYYYMMDD to DD/MM/YYYY
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      
      return `${day}/${month}/${year}`;
    },
    
    formatDateDashes(dateStr) {
      if (!dateStr) return '';
      // Date is already in YYYY-MM-DD format, return as is
      return dateStr;
    },
    
    getDirectionText(direction) {
      // Convert direction code to text
      if (direction === '1') return 'Alcista';
      if (direction === '0') return 'Bajista';
      return 'Desconocido';
    },
    
    getEntryTypeText(typeCode) {
      // Convert entry type code to text
      if (typeCode === '0') return 'PER';
      if (typeCode === '1') return 'RR';
      if (typeCode === '2') return 'RRA';
      if (typeCode === '3') return 'LT';
      if (typeCode === '4') return 'LTA';
      return 'Desconocido';
    },
    
    updateChartUrl() {
      if (this.ticker) {
        this.chartUrl = `${process.env.VUE_APP_API}/stockchart?s=${this.ticker}&hide_buttons=1`;
      }
    },
    
    handleIframeLoad() {
      setTimeout(() => {
        this.isIframeLoading = false;
      }, 400);
    },
    
    fetchAndSetStockData() {
      if (!this.ticker) return;
      
      this.loading = true;
      this.error = null;
      
      // Use the correct endpoints for fetching stock data
      const primaryUrl = `${process.env.VUE_APP_API}/api/v1/stock-finder/stocks/symbol/${this.ticker}`;
      const fallbackUrl = `${process.env.VUE_APP_API}/api/v1/stock-finder/stocks/symbol-without-entry/${this.ticker}`;

      axios.get(primaryUrl)
        .then(response => {
          if (response.data && response.data.length > 0) {
             // Set the ticker in the Vuex store
             this.setSelectedTicker(response.data[0]);
             this.loading = false;
          } else {
            // If primary endpoint returns empty, try the fallback for non-eurekers stocks
            return axios.get(fallbackUrl);
          }
        })
        .then(fallbackResponse => {
          // This only executes if primary call returned empty and the fallback was called
          if (fallbackResponse && fallbackResponse.data) {
             const stockData = {
               ...fallbackResponse.data,
               isEureker: false
             };
             this.setSelectedTicker(stockData);
             this.loading = false;
          } else if (fallbackResponse) { 
             // Both endpoints failed or returned empty
             console.error('Error fetching stock data: Ticker not found', this.ticker);
             this.error = new Error('Ticker not found');
             this.loading = false;
          }
        })
        .catch(err => {
           console.error('Error fetching stock data:', err);
           this.error = err;
           this.loading = false;
        });
    },
    
    viewChart() {
      window.open(this.chartUrl, '_blank');
    },
    
    openDynamicChart() {
      window.open(`/app/graficador-dinamico/ticker/${this.ticker}`, '_blank');
    },
    
    // Métodos para el componente de favoritos
    addToFavorites() {
      this.showWorkspaces = true;
    },
    
    _closeFavoriteWorkspacesWindow() {
      this.showWorkspaces = false;
    },
    
    // Métodos para el componente de valores en espera
    addToWatchlist() {
      this.showWaiting = true;
    },
    
    _closeWaitingValueWindow() {
      this.showWaiting = false;
    },
    
    _onWaitingAdded() {
    }
  }
};
</script>

<style scoped>
.stock-thread-header {
  background-color: var(--color-surface);
  border-radius: 0;
  overflow: hidden;
  margin-bottom: 20px;
}

.stock-chart-container {
  width: 100%;
  height: 400px;
  background-color: var(--color-background);
  border-bottom: 1px solid var(--color-outline);
  position: relative;
}

.stock-chart-iframe {
  width: 100%;
  height: 100%;
  border: none;
  position: relative;
  z-index: 2;
  transition: opacity 0.5s ease-in-out;
}

/* Iframe loading/loaded states for fade effect */
.iframe-loading {
  opacity: 0;
}

.iframe-loaded {
  opacity: 1;
}

/* Animación de carga del iframe */
.iframe-loading-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--color-custom-stockchart-background, var(--color-background));
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.wave {
  width: 5px;
  height: 100px;
  background: linear-gradient(45deg, var(--primary-light, #4E6E82), var(--primary, #245275));
  margin: 10px;
  animation: wave 1s ease-in-out infinite;
  border-radius: 20px;
}

.wave:nth-child(2) {
  animation-delay: 0.1s;
}

.wave:nth-child(3) {
  animation-delay: 0.2s;
}

@keyframes wave {
  0% {
    transform: scale(0.3);
  }
  50% {
    transform: scale(1);
  }
  100% {
    transform: scale(0.3);
  }
}

.stock-info-container {
  padding: 20px;
}

.stock-info-section {
  margin-bottom: 25px;
}

.section-title {
  margin-top: 0;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--color-outline);
  color: var(--color-highlight);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.info-label {
  font-size: 0.9rem;
  color: var(--color-text-secondary);
}

.info-value {
  font-weight: 500;
  color: var(--color-text-primary);
}

.positive-variation {
  color: #4caf50;
}

.negative-variation {
  color: #f44336;
}

.stock-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.action-btn:hover {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

.action-btn i {
  font-size: 18px;
}

.view-chart-btn, .open-dynamic-btn {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

.view-chart-btn:hover, .open-dynamic-btn:hover {
  background-color: var(--color-highlight-dark, #1976d2);
}

.loading-state, .error-state {
  padding: 40px 20px;
  text-align: center;
}

.error-state {
  color: #f44336;
}

/* Responsive design */
@media (max-width: 768px) {
  .info-grid {
    grid-template-columns: 1fr 1fr;
  }
  
  .stock-actions {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style> 